// ** MUI Imports
import { alpha, Theme } from '@mui/material/styles'
import { getAppCssVars } from './appCssVars'

const GlobalStyles = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light'

  const perfectScrollbarRail = {
    zIndex: 1,
    '&:hover, &:focus, &.ps--clicking': {
      backgroundColor: isLight ? '#F1F0F5 !important' : '#222B36 !important',
    },
  }
  const perfectScrollbarThumb = {
    backgroundColor: isLight
      ? 'rgba(93, 89, 108, 0.2) !important'
      : 'rgba(230, 235, 240, 0.3) !important',
  }

  return {
    ':root': getAppCssVars(theme.palette.mode),

    '#root': {
      [theme.breakpoints.up('xl')]: {
        fontSize: '1rem',
      },
      [theme.breakpoints.down('xl')]: {
        fontSize: '0.9rem',
      },
    },

    '::-webkit-scrollbar': {
      width: 8,
      height: 10,
    },
    '::-webkit-scrollbar-track': {
      borderRadius: 20,
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: 'var(--app-scrollbar-thumb)',
      border: '1px solid var(--app-scrollbar-thumb-border)',
      borderRadius: 20,
    },

    code: {
      color: 'var(--app-code-color)',
    },

    // Popovers and menus portal to <body>, outside the layout's glass block,
    // so this is the one place that can give them the glass surface.
    '.MuiPopover-paper, .MuiMenu-paper': {
      backgroundColor: 'var(--app-popover-surface)',
      backgroundImage: 'none',
      border: '1px solid var(--app-glass-border)',
      backdropFilter: 'var(--app-popover-blur)',
      WebkitBackdropFilter: 'var(--app-popover-blur)',
    },

    // reactflow/dist/style.css (v11, imported in App.tsx) hardcodes light-only
    // colours (white panels, light-grey edges, near-black handles). Emotion
    // injects after that stylesheet, so these same-specificity rules win.
    '.react-flow__controls': {
      boxShadow: 'var(--app-widget-shadow)',
    },
    '.react-flow__controls-button': {
      background: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& svg': {
        fill: theme.palette.text.primary,
      },
      '&:hover': {
        // action.hover is translucent; layer it over the paper so the button
        // stays opaque above the canvas dots.
        backgroundColor: theme.palette.background.paper,
        backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
      },
    },
    '.react-flow__minimap': {
      backgroundColor: theme.palette.background.paper,
    },
    '.react-flow__attribution': {
      background: alpha(theme.palette.background.paper, 0.5),
      '& a': {
        color: theme.palette.text.secondary,
      },
    },
    '.react-flow__edge-path, .react-flow__connection-path': {
      stroke: isLight ? '#b1b1b7' : '#5c6478', // color-ok: reactflow default edge colours
    },
    '.react-flow__edge.selected .react-flow__edge-path, .react-flow__edge:focus .react-flow__edge-path, .react-flow__edge:focus-visible .react-flow__edge-path':
      {
        stroke: theme.palette.text.primary,
      },
    '.react-flow__edge-textbg': {
      fill: theme.palette.background.paper,
    },
    '.react-flow__handle': {
      background: theme.palette.text.secondary,
      borderColor: theme.palette.background.paper,
    },

    '.demo-space-x > *': {
      marginTop: '1rem !important',
      marginRight: '1rem !important',
      'body[dir="rtl"] &': {
        marginRight: '0 !important',
        marginLeft: '1rem !important',
      },
    },
    '.demo-space-y > *:not(:last-of-type)': {
      marginBottom: '1rem',
    },
    '.MuiGrid-container.match-height .MuiCard-root': {
      height: '100%',
    },
    '.ps__rail-y': {
      ...perfectScrollbarRail,
      left: 'auto !important',
      '& .ps__thumb-y': {
        ...perfectScrollbarThumb,
        right: '3px !important',
        left: 'auto !important',
      },
      '.layout-vertical-nav &': {
        '& .ps__thumb-y': {
          width: 4,
        },
        '&:hover, &:focus, &.ps--clicking': {
          backgroundColor: 'transparent !important',
          '& .ps__thumb-y': {
            width: 6,
          },
        },
      },
    },
    '.ps__rail-x': {
      ...perfectScrollbarRail,
      top: 'auto !important',
      '& .ps__thumb-x': {
        ...perfectScrollbarThumb,
        bottom: '3px !important',
        top: 'auto !important',
      },
    },

    '#nprogress': {
      pointerEvents: 'none',
      '& .bar': {
        left: 0,
        top: 0,
        height: 3,
        width: '100%',
        zIndex: 2000,
        position: 'fixed',
        backgroundColor: theme.palette.primary.main,
      },
    },
    '.tiptap.ProseMirror-focused': {
      outline: 'none',
    },
    '.tiptap .is-empty::before, .tiptap h1.is-empty:nth-child(1)::before': {
      color: theme.palette.text.disabled,
    },
  }
}

export default GlobalStyles
